import { TextField, Checkbox } from '@mui/material'
import { DynamicContent, MultiselectList, Pagination } from 'shared/components'
import { PaginationProps } from 'shared/hooks'
import { useTranslation } from 'react-i18next'
import {
  ButtonWrapper,
  PaginationWrapper,
  SearchWrapper,
  Container,
  ListWrapper,
  CheckboxWrapper,
} from './EditList.style'
import { LoadingButton } from '@mui/lab'
import { ExtendedOption } from 'shared/types'
import type { KeyboardEvent, ReactNode } from 'react'

interface EditListDialogProps<T, K> {
  query: string
  setQuery: (value: string) => void
  dataLoading: boolean
  options: ExtendedOption<T, K>[]
  selectedValues: K[]
  handleItemClick: (value: K) => void
  paginationProps: PaginationProps
  isSaving: boolean
  onSave: () => void
  saveDisabled: boolean
  onToggleAll?: (checked: boolean) => void
  allSelected?: boolean
  actionButtonKey?: string
  searchPlaceholder?: string
  searchHelperText?: string
  onSearchKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void
  headerContent?: ReactNode
}

const EditList = <T, K>({
  query,
  setQuery,
  dataLoading,
  options,
  selectedValues,
  handleItemClick,
  paginationProps,
  isSaving,
  onSave,
  saveDisabled,
  onToggleAll,
  allSelected,
  actionButtonKey,
  searchPlaceholder,
  searchHelperText,
  onSearchKeyDown,
  headerContent,
}: EditListDialogProps<T, K>) => {
  const { t } = useTranslation()

  return (
    <Container>
      <SearchWrapper>
        <TextField
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder={searchPlaceholder}
          helperText={searchHelperText}
          size="small"
        />
      </SearchWrapper>
      {headerContent}
      {onToggleAll && (
        <CheckboxWrapper
          control={
            <Checkbox
              edge="start"
              tabIndex={-1}
              checked={allSelected}
              onChange={(e, checked) => onToggleAll(checked)}
            />
          }
          label={t('selectAll')}
        />
      )}
      <ListWrapper>
        <DynamicContent
          loading={dataLoading && options.length === 0}
          textAlign="center"
          displayContent={options.length}
        >
          <MultiselectList
            options={options}
            selectedValues={selectedValues}
            onItemClick={handleItemClick}
          />
        </DynamicContent>
      </ListWrapper>
      <PaginationWrapper>
        <Pagination {...paginationProps} />
      </PaginationWrapper>
      <ButtonWrapper>
        <LoadingButton
          variant="contained"
          onClick={onSave}
          loading={isSaving || dataLoading}
          disabled={saveDisabled}
        >
          {t(actionButtonKey ?? 'save')}
        </LoadingButton>
      </ButtonWrapper>
    </Container>
  )
}

export default EditList
